import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Heading, HStack, Spinner } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIntercom } from "react-use-intercom";
import {
  type LibraryElementContentDto,
  LibraryElementTypeDto,
  type SelectionCriterionDto,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { ApplyTemplateButton } from "../components/apply-template/ApplyTemplateButton";
import {
  QualificationRequirementDrawer,
  type QualificationFormValues,
} from "../components/QualificationRequirementDrawer";
import { ExclusionGroundsSection } from "../components/ExclusionGroundsSection";
import { EspdChoiceField } from "../components/espd/EspdChoiceField";
import { isFoaPartII, usesEspd } from "../utils/foaPartII";
import { QualificationRequirementList } from "../components/QualificationRequirementList";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { AddedFromLibraryBanner } from "../../../../../common/library/AddedFromLibraryBanner";
import { AddRequirementPanel } from "../../../../../common/library/AddRequirementPanel";
import { useAddFromLibrary } from "../../../../../common/library/useAddFromLibrary";
import { SectionEmptyState } from "../../../../../common/library/SectionEmptyState";

// t('Delete requirement') t('Are you sure you want to delete this requirement?') t('Delete')
// t('Add qualification requirements') t('qualification requirements')
// t('New requirement') t('From the library')
// t('No qualification requirements in this procurement')

type DrawerMode =
  | { kind: "edit"; index: number; initialValues: QualificationFormValues };

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  isActive: boolean;
}

export const QualificationStep = ({ eventId, previousStep, nextStep, nextStepLabel, isActive }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const [drawerState, setDrawerState] = useState<DrawerMode | null>(null);
  const [drawerKey, setDrawerKey] = useState(0);
  const confirmationModal = useConfirmationModal();
  const { update: updateIntercom } = useIntercom();

  const items = useMemo(() => state.eventData.selectionCriteria ?? [], [state.eventData.selectionCriteria]);
  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;
  const buyerChoosesEspd = isFoaPartII(publicContext?.regulation, publicContext?.foaPart);
  const espdInUse = publicContext !== null && usesEspd(publicContext);
  const isSavingCriteria = (() => {
    const s = fieldStatus("eventData.selectionCriteria");
    return s === "saving" || s === "pending";
  })();

  useEffect(() => {
    if (!isActive) setDrawerState(null);
  }, [isActive]);

  const isDrawerOpen = drawerState !== null;
  useEffect(() => {
    updateIntercom({ hideDefaultLauncher: isDrawerOpen });
  }, [isDrawerOpen, updateIntercom]);

  const commit = useCallback(
    (next: SelectionCriterionDto[]) => updateField({ eventData: { selectionCriteria: next } }),
    [updateField]
  );

  const library = useAddFromLibrary({ items, commit });
  const { save } = library;

  const handleSubmit = useCallback(
    async (values: SelectionCriterionDto) => {
      const newList =
        drawerState?.kind === "edit"
          ? items.map((item, i) => (i === drawerState.index ? values : item))
          : [...items, values];
      save(newList);
      setDrawerState(null);
    },
    [items, save, drawerState],
  );

  // A batch is one edit, so Undo restores the list as it was rather than subtracting rows afterwards.
  const addFromLibrary = (contents: LibraryElementContentDto[]) =>
    library.add(contents.flatMap((c) => (c.selection ? [c.selection] : [])));


  const handleEdit = useCallback(
    (index: number) => {
      const item = items[index];
      setDrawerKey((k) => k + 1);
      setDrawerState({
        kind: "edit",
        index,
        initialValues: {
          type: item.type,
          name: item.name ?? "",
          description: item.description,
          requiresEvidence: item.requiresEvidence,
        },
      });
    },
    [items],
  );

  const handleDelete = useCallback(
    (index: number) => {
      confirmationModal.show({
        title: t("Delete requirement"),
        description: t("Are you sure you want to delete this requirement?"),
        confirmButtonText: t("Delete") ?? "",
        confirmButtonColor: "red",
        onConfirmed: () => {
          save(items.filter((_, i) => i !== index));
        },
      });
    },
    [items, save, confirmationModal, t],
  );

  return (
    <Box pb="8" pt="10">
      <Flex justify="space-between" align="center" mb="6">
        <Heading as="h2" size="md">
          {t("Qualification requirements")} {isSavingCriteria && <Spinner size="xs" />}
        </Heading>
        <HStack spacing="2" flexShrink={0}>
          {/* Both ways to fill the section are one click away; an empty section carries them instead. */}
          {items.length > 0 && (
            <>
              <Button size="sm" variant="outline" colorScheme="teal" leftIcon={<AddIcon />} onClick={() => library.openPanel("new")}>
                {t("New requirement")}
              </Button>
              <Button size="sm" colorScheme="teal" onClick={() => library.openPanel("library")}>
                {t("From the library")}
              </Button>
            </>
          )}
          <ApplyTemplateButton eventId={eventId} preselect={["selection"]} />
        </HStack>
      </Flex>

      {library.lastAdded && <AddedFromLibraryBanner count={library.lastAdded.count} onUndo={library.undo} />}

      <Box mb="8">
        {items.length === 0 ? (
          <SectionEmptyState
            title={t("No qualification requirements in this procurement")}
            type={LibraryElementTypeDto.Selection}
            onAddFromLibrary={() => library.openPanel("library")}
            onWriteNew={() => library.openPanel("new")}
          />
        ) : (
          <QualificationRequirementList
            items={items}
            onAdd={() => library.openPanel("library")}
            onEdit={handleEdit}
            onDelete={handleDelete}
            markedCount={library.markedCount}
          />
        )}
      </Box>

      {buyerChoosesEspd && <EspdChoiceField usesEspd={espdInUse} onChange={(patch) => updateField(patch)} />}

      {espdInUse && <ExclusionGroundsSection eventId={eventId} />}

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />

      <AddRequirementPanel
          isOpen={library.isPanelOpen}
          onClose={library.closePanel}
          type={LibraryElementTypeDto.Selection}
          title={t("Add qualification requirements")}
          typeNoun={t("qualification requirements")}
          initialTab={library.tab}
          isAlreadyInSection={(el) => items.some((i) => i.name === el.content.selection?.name)}
          onAdd={addFromLibrary}
        />

      <QualificationRequirementDrawer
        key={drawerKey}
        isOpen={drawerState !== null}
        isSaving={isSavingCriteria}
        mode={drawerState?.kind ?? "add"}
        defaultValues={drawerState?.kind === "edit" ? drawerState.initialValues : undefined}
        onSubmit={handleSubmit}
        onClose={() => setDrawerState(null)}
      />

      {confirmationModal.modal}
    </Box>
  );
};
